import { BrushSizeSchema } from "#schemas/brush.ts";
import { FpsSchema, HexColorSchema, OnionOpacitySchema, ProjectNameSchema } from "#schemas/primitives.ts";
import { collectStateIds, MachineLayoutSchema, StateMachineSchema } from "#schemas/pxpf.ts";
import { LayerSchema } from "#schemas/pxpf.ts";
import { domainRegistry } from "#schemas/registry.ts";
import { StateFrameRangesSchema } from "#schemas/state.ts";
import { ToolModeSchema } from "#schemas/tool.ts";
import { z } from "zod";

const FrameSchema = z.array(LayerSchema);

/**
 * Runtime store invariants. Used as a *post-update validator* by the dev
 * subscribe gate, not as a setter argument type.
 */
export const StoreSnapshotSchema = z.object({
  color: HexColorSchema,
  fps: FpsSchema,
  onionOpacity: OnionOpacitySchema,
  projectName: ProjectNameSchema,
  brushSize: BrushSizeSchema,
  mode: ToolModeSchema,
  frames: z.array(FrameSchema).min(1),
  frameIds: z.array(z.string()),
  thumbs: z.array(z.string()),
  activeFrame: z.int().min(0),
  activeLayerId: z.string(),
  stateFrames: StateFrameRangesSchema,
  stateMachine: StateMachineSchema.nullable(),
  machineLayout: MachineLayoutSchema,
}).check((ctx) => {
  const v = ctx.value;
  if (v.frameIds.length !== v.frames.length) {
    ctx.issues.push({
      code: "custom",
      path: ["frameIds"],
      message: `frameIds.length (${v.frameIds.length}) !== frames.length (${v.frames.length})`,
      input: v.frameIds,
    });
  }
  if (v.thumbs.length !== v.frames.length) {
    ctx.issues.push({
      code: "custom",
      path: ["thumbs"],
      message: `thumbs.length (${v.thumbs.length}) !== frames.length (${v.frames.length})`,
      input: v.thumbs,
    });
  }
  if (v.activeFrame >= v.frames.length) {
    ctx.issues.push({
      code: "custom",
      path: ["activeFrame"],
      message: `activeFrame ${v.activeFrame} >= frames.length ${v.frames.length}`,
      input: v.activeFrame,
    });
  }
  const active = v.frames[v.activeFrame];
  if (active && !active.some((l) => l.id === v.activeLayerId)) {
    ctx.issues.push({
      code: "custom",
      path: ["activeLayerId"],
      message: `activeLayerId "${v.activeLayerId}" not present in active frame's layers`,
      input: v.activeLayerId,
    });
  }
  for (const [id, range] of Object.entries(v.stateFrames)) {
    if (range[1] >= v.frames.length) {
      ctx.issues.push({
        code: "custom",
        path: ["stateFrames", id],
        message: `range end ${range[1]} >= frames.length ${v.frames.length}`,
        input: range,
      });
    }
  }
  if (v.stateMachine) {
    const machineIds = collectStateIds(v.stateMachine);
    for (const id of Object.keys(v.stateFrames)) {
      if (!machineIds.has(id)) {
        ctx.issues.push({
          code: "custom",
          path: ["stateFrames", id],
          message: `stateFrames key "${id}" is not a state in stateMachine`,
          input: id,
        });
      }
    }
    for (const id of machineIds) {
      if (!(id in v.stateFrames)) {
        ctx.issues.push({
          code: "custom",
          path: ["stateFrames"],
          message: `stateMachine references state "${id}" but stateFrames has no entry for it`,
          input: id,
        });
      }
    }
  }
}).register(domainRegistry, {
  id: "store_snapshot",
  title: "Store snapshot",
  description: "Validated runtime store state. Used as a post-update sanity gate, not as a setter parser.",
  surface: "store",
  llmExposed: false,
  group: "project",
});
export type StoreSnapshot = z.infer<typeof StoreSnapshotSchema>;

/** Clamp a candidate active-frame index to [0, frameCount-1]. */
export function clampActiveFrame(activeFrame: number, frameCount: number): number {
  if (frameCount <= 0) return 0;
  if (!Number.isFinite(activeFrame)) return 0;
  if (activeFrame < 0) return 0;
  if (activeFrame >= frameCount) return frameCount - 1;
  return Math.floor(activeFrame);
}

/** Pick `requested` if it exists in `layers`, else first layer's id, else `fallback`. */
export function resolveActiveLayerId(
  layers: readonly { id: string }[],
  requested: string,
  fallback = "L1",
): string {
  if (layers.some((l) => l.id === requested)) return requested;
  return layers[0]?.id ?? fallback;
}
